import { memo, useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { OnboardingScreen } from './OnboardingScreen';
import { ActivationScreen } from './ActivationScreen';
import { LoadingScreen } from './LoadingScreen';
import { ErrorScreen } from './ErrorScreen';
import { MainLayout } from './MainLayout';
import { useApp } from '../contexts/AppContext';
import * as Sentry from "@sentry/react";

export const MainApp = memo(function MainApp() {
  // Get all data and actions from context
  const {
    userInfo,
    userConfig,
    error,
    isLoading,
    isAuthenticated,
    isPaymentLoading,
    completeOnboarding,
    handlePremiumClick,
    handleBalanceUpdate
  } = useApp();

  // State to manage stable view transitions with a more complex state machine
  const [viewState, setViewState] = useState<{
    current: string;
    prev: string | null;
    loading: boolean;
    timestamp: number;
  }>({
    current: 'loading',
    prev: null,
    loading: true,
    timestamp: Date.now()
  });

  // Update state with animation-friendly delayed transitions
  useEffect(() => {
    let nextView = 'loading';
    
    // Determine the correct view based on app state
    if (error) {
      nextView = 'error';
      // Report error to Sentry when error state is set
      Sentry.captureMessage(`MainApp error: ${error}`, {
        level: "error",
        tags: {
          component: "MainApp"
        }
      });
    } else if (isLoading || isPaymentLoading) {
      nextView = 'loading';
    } else if (!isAuthenticated || !userInfo) {
      nextView = 'auth_required';
    } else if (!userConfig || !userConfig.onboarding_completed) {
      nextView = 'onboarding';
    } else if (!userInfo.activated) {
      nextView = 'activation';
    } else {
      nextView = 'main';
    }
    
    // Don't update if view hasn't changed
    if (nextView === viewState.current) return;
    
    // Ensure minimum time between transitions to prevent flickering
    const now = Date.now();
    const timeSinceLastChange = now - viewState.timestamp;
    const MIN_TRANSITION_TIME = 300; // reduced from 700ms
    
    if (timeSinceLastChange < MIN_TRANSITION_TIME) {
      // Schedule delayed transition
      const delay = MIN_TRANSITION_TIME - timeSinceLastChange;
      const timeoutId = setTimeout(() => {
        setViewState({
          current: nextView,
          prev: viewState.current,
          loading: false,
          timestamp: now + delay
        });
      }, delay);
      
      // Clean up timeout if component unmounts
      return () => clearTimeout(timeoutId);
    } else {
      // Change immediately
      setViewState({
        current: nextView,
        prev: viewState.current,
        loading: false,
        timestamp: now
      });
    }
  }, [error, isLoading, isPaymentLoading, isAuthenticated, userInfo, userConfig, viewState]);

  // Render appropriate view based on current state
  switch (viewState.current) {
    case 'error':
      return <ErrorScreen message={error || 'Unknown error'} />;
      
    case 'loading':
      return <LoadingScreen />;
      
    case 'auth_required':
      return <ErrorScreen message="Authentication required" />;
      
    case 'onboarding':
      return <OnboardingScreen onComplete={completeOnboarding} />;
      
    case 'activation':
      if (!userInfo) return <LoadingScreen />;
      return (
        <ActivationScreen 
          userInfo={userInfo}
          onPremiumClick={handlePremiumClick}
          isLoading={isLoading || isPaymentLoading}
        />
      );
      
    case 'main':
      if (!userInfo) return <LoadingScreen />;
      return (
        <Routes>
          <Route path="/*" element={
            <MainLayout 
              userInfo={userInfo} 
              onBalanceUpdate={handleBalanceUpdate} 
            />
          } />
        </Routes>
      );
      
    default:
      return <LoadingScreen />;
  }
});